/* eslint-disable react/no-unused-prop-types */
import {
  isNumber,
  parseChildrenArray,
  useClickOutsideEvent,
  useDerivedState,
  useModal,
} from '@/utils'
import { ChangeEvent, ReactNode, useState } from 'react'
import { Input } from '../../styled'
import { OptionNode } from './OptionNode'
import { filterOptions, getTextFromOptionValue } from './helpers'
import {
  Container,
  DropdownIcon,
  InputContainer,
  OptionsContainer,
  StyledOption,
} from './styles'

interface SelectProps {
  value?: string | number
  id?: string
  onSelect: ((value?: string) => void) | ((value?: number) => void)
  onSearch?: (value: string) => void
  onBlur?: () => void
  onIconError?: string
  children: ReactNode
  error?: boolean
  disabled?: boolean
  onFocus?: () => void
  autocomplete?: boolean
  placeholder?: string
  readOnly?: boolean
}

export const Option: React.ComponentType<any> = () => null

export const Select = ({
  value = '',
  onSelect,
  onSearch,
  onBlur,
  onIconError,
  children,
  error,
  disabled,
  onFocus,
  autocomplete = false,
  ...props
}: SelectProps) => {
  const parsedChildren = parseChildrenArray(children)
  const [ref, setRef] = useState<HTMLDivElement | null>(null)

  const [inputValue, setInputValue] = useDerivedState(
    isNumber(value) ? value : value || '',
    autocomplete
      ? undefined
      : () => getTextFromOptionValue(parsedChildren, value),
  )

  const { isOpen, open, close } = useModal()

  const options = autocomplete
    ? filterOptions(String(inputValue), parsedChildren)
    : parsedChildren

  const handleFocus = () => {
    if (!disabled) open()
    onFocus?.()
  }
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    if (onSearch) onSearch(newValue)
  }

  const handleBlur = () => {
    if (
      !autocomplete &&
      value &&
      inputValue !== getTextFromOptionValue(parsedChildren, value)
    ) {
      onSelect()
    }
    onBlur?.()
    close()
  }

  const handleSelect = (val: string | number) => {
    if (typeof val === 'number') {
      ;(onSelect as (value?: number) => void)(val)
    } else {
      ;(onSelect as (value?: string) => void)(val)
    }
    close()
  }

  const handleTabClick = (event: KeyboardEvent) => {
    if (event.key === 'Tab') close()
  }

  useClickOutsideEvent(ref, handleBlur, [inputValue, onBlur])

  return (
    <Container
      ref={setRef}
      isOpen={isOpen && (!autocomplete || options.length > 0)}
    >
      <InputContainer>
        <Input
          disabled={disabled}
          error={error}
          onChange={handleChange}
          readOnly
          value={String(inputValue)}
          {...props}
          onFocus={handleFocus}
          onKeyDown={(e) => handleTabClick(e.nativeEvent)}
        />
        {!autocomplete && <DropdownIcon onClick={handleFocus} />}
      </InputContainer>
      {isOpen && Boolean(!autocomplete || options.length) && (
        <OptionsContainer>
          {options.length
            ? options.map(
                ({
                  key,
                  value: optionValue,
                  children: text,
                  icon,
                  disabled: optionDisabled,
                }: any) => (
                  <OptionNode
                    key={key}
                    disabled={disabled || optionDisabled}
                    icon={icon}
                    onClick={handleSelect}
                    onIconError={onIconError}
                    selected={text === inputValue}
                    text={text}
                    value={optionValue}
                  />
                ),
              )
            : !autocomplete && <StyledOption>No options.</StyledOption>}
        </OptionsContainer>
      )}
    </Container>
  )
}
